import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../utils/axiosConfig';
import {
  Box,
  Typography,
  TextField,
  Collapse,
  InputAdornment,
  Chip,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';

// Category color configuration for visual distinction
const categoryConfig = {
  recipient: {
    color: '#3B82F6',
    bgColor: '#EFF6FF',
    borderColor: '#BFDBFE',
    icon: PersonOutlineIcon,
  },
  case: {
    color: '#8B5CF6',
    bgColor: '#F5F3FF',
    borderColor: '#DDD6FE',
    icon: FolderOutlinedIcon,
  },
  client: {
    color: '#0D9488',
    bgColor: '#CCFBF1',
    borderColor: '#99F6E4',
    icon: BadgeOutlinedIcon,
  },
  lawyer: {
    color: '#0EA5E9',
    bgColor: '#E0F2FE',
    borderColor: '#BAE6FD',
    icon: BadgeOutlinedIcon,
  },
  other: {
    color: '#10B981',
    bgColor: '#ECFDF5',
    borderColor: '#A7F3D0',
    icon: SendOutlinedIcon,
  },
  sender: {
    color: '#10B981',
    bgColor: '#ECFDF5',
    borderColor: '#A7F3D0',
    icon: SendOutlinedIcon,
  },
  date: {
    color: '#F59E0B',
    bgColor: '#FFFBEB',
    borderColor: '#FDE68A',
    icon: CalendarTodayOutlinedIcon,
  },
};

const TagSidebar = ({ onTagClick }) => {
  const { t } = useTranslation(['forms', 'buttons']);
  const [tags, setTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    case: false,
    client: false,
    lawyer: false,
    other: false,
    date: false,
  });

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await axios.get('/api/email-templates/tags/all');
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleTagClick = (tagName) => {
    if (onTagClick) {
      onTagClick(tagName);
    }
  };

  const handleCategoryToggle = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Map legacy 'sender' category to 'other' for display
  const mapCategory = (cat) => (cat === 'sender' ? 'other' : cat);

  // Group tags by category
  const groupedTags = tags.reduce((acc, tag) => {
    const category = mapCategory(tag.category || 'custom');
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(tag);
    return acc;
  }, {});

  // Filter tags by search term
  const filteredTags = tags.filter(tag =>
    tag.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tag.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group filtered tags
  const filteredGroupedTags = filteredTags.reduce((acc, tag) => {
    const category = mapCategory(tag.category || 'custom');
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(tag);
    return acc;
  }, {});

  // Auto-expand categories that have matching tags when searching
  useEffect(() => {
    if (searchTerm.trim()) {
      const categoriesWithMatches = Object.keys(filteredGroupedTags);
      setExpandedCategories(prev => {
        const newState = { ...prev };
        categoriesWithMatches.forEach(category => {
          newState[category] = true;
        });
        return newState;
      });
    } else {
      // Collapse all when search is cleared
      setExpandedCategories({
        case: false,
        client: false,
        lawyer: false,
        other: false,
        date: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const categories = [
    // Hide recipient placeholders for now (still exist in backend)
    { key: 'case', label: t('forms:tags.categories.case') },
    { key: 'client', label: t('forms:tags.categories.client') },
    { key: 'lawyer', label: t('forms:tags.categories.lawyer') },
    { key: 'other', label: t('forms:tags.categories.other') },
    { key: 'date', label: t('forms:tags.categories.date') },
  ];

  const displayGroups = searchTerm ? filteredGroupedTags : groupedTags;

  return (
    <>
      <Box sx={{ 
        width: "260px", 
        borderRight: "1px solid #E5E7EB", 
        backgroundColor: "#FAFBFC",
        minHeight: "600px",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#fff',
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: "13px", 
              fontWeight: 600, 
              color: '#1F2937',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              mb: 1.5,
            }}
          >
            {t('forms:tags.title')}
          </Typography>
          
          <TextField
            fullWidth
            size="small"
            placeholder={t('forms:tags.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '18px', color: '#9CA3AF' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                height: '36px',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3B82F6',
                  borderWidth: '1.5px',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '13px',
                color: '#374151',
                '&::placeholder': {
                  color: '#9CA3AF',
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* Categories List */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 1.5,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#D1D5DB',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#9CA3AF',
          },
        }}>
          {categories.map((category) => {
            // Filter out deprecated/hidden tags and email-role tags we don't show in UI
            const rawCategoryTags = displayGroups[category.key] || [];
            const categoryTags = rawCategoryTags.filter((tag) => {
              if (tag.name === 'caseName') return false;
              // Hide recipient* and from* placeholders from the UI
              if (tag.name.startsWith('recipient')) return false;
              if (tag.name.startsWith('from')) return false;
              // Hide address-only placeholders for lawyers and parties
              if (
                tag.name === 'responsibleLawyerAddress' ||
                tag.name === 'treatingLawyerAddress' ||
                tag.name === 'opposingPartyAddress' ||
                tag.name === 'payingPartyAddress' ||
                tag.name === 'advocateOpposingPartyAddress'
              ) {
                return false;
              }
              return true;
            });

            const config = categoryConfig[category.key];
            const IconComponent = config.icon;

            return (
              <Box 
                key={category.key} 
                sx={{ 
                  mb: 1.5,
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  overflow: "hidden",
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                  border: `1px solid ${expandedCategories[category.key] ? config.borderColor : '#E5E7EB'}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                  },
                }}
              >
                {/* Category Header */}
                <Box
                  onClick={() => handleCategoryToggle(category.key)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1.25,
                    cursor: "pointer",
                    backgroundColor: expandedCategories[category.key] ? config.bgColor : '#fff',
                    transition: 'all 0.2s ease',
                    "&:hover": { 
                      backgroundColor: config.bgColor,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '6px',
                      backgroundColor: config.bgColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.25,
                      border: `1px solid ${config.borderColor}`,
                    }}
                  >
                    <IconComponent sx={{ fontSize: '16px', color: config.color }} />
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: "13px", 
                      flex: 1, 
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    {category.label}
                  </Typography>
                  <Chip
                    label={categoryTags.length}
                    size="small"
                    sx={{
                      height: '20px',
                      minWidth: '24px',
                      fontSize: '11px',
                      fontWeight: 500,
                      backgroundColor: config.bgColor,
                      color: config.color,
                      border: `1px solid ${config.borderColor}`,
                      mr: 0.5,
                      '& .MuiChip-label': {
                        px: 0.75,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.2s ease',
                      transform: expandedCategories[category.key] ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                  >
                    {expandedCategories[category.key] ? 
                      <ExpandLessIcon sx={{ fontSize: '18px', color: '#6B7280' }} /> : 
                      <ExpandMoreIcon sx={{ fontSize: '18px', color: '#6B7280' }} />
                    }
                  </Box>
                </Box>

                {/* Placeholder Items */}
                <Collapse in={expandedCategories[category.key]} timeout={200}>
                  <Box sx={{ 
                    p: 1,
                    pt: 0.5,
                    backgroundColor: '#FAFBFC',
                    borderTop: `1px solid ${config.borderColor}`,
                  }}>
                    {categoryTags.map((tag, index) => {
                      // Override display labels for some system tags without changing backend
                      let displayLabel = tag.displayName;
                      if (tag.name === 'caseStatus') {
                        displayLabel = 'Case File Status';
                      } else if (tag.name === 'caseType') {
                        displayLabel = 'Case Jurisdiction';
                      }

                      return (
                      <Box
                        key={tag._id}
                        onClick={() => handleTagClick(tag.name)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          py: 0.75,
                          px: 1,
                          mt: index === 0 ? 0.5 : 0,
                          mb: 0.5,
                          borderRadius: "6px",
                          cursor: "pointer",
                          backgroundColor: "#fff",
                          border: "1px solid #E5E7EB",
                          transition: 'all 0.15s ease',
                          "&:hover": {
                            backgroundColor: config.bgColor,
                            borderColor: config.borderColor,
                            transform: 'translateX(2px)',
                          },
                          "&:active": {
                            transform: 'translateX(2px) scale(0.99)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: config.color,
                            mr: 1,
                            opacity: 0.6,
                          }}
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: "12px", 
                            flex: 1,
                            color: '#4B5563',
                            fontWeight: 400,
                          }}
                        >
                          {displayLabel}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '10px',
                            color: '#9CA3AF',
                            opacity: 0,
                            transition: 'opacity 0.15s ease',
                            '.MuiBox-root:hover &': {
                              opacity: 1,
                            },
                          }}
                        >
                          Click to insert
                        </Typography>
                      </Box>
                    )})}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default TagSidebar;
